import { useEmployeeActivity, type ActivityItem } from "@/hooks/use-employee-activity";
import { Card, CardContent } from "@/components/ui/card";
import { SrIcon } from "@/components/ui/SrIcon";
import { Link } from "react-router-dom";
import { formatDate } from "@/lib/format";

const ICON: Record<ActivityItem["type"], string> = {
  time_entry: "clock",
  task_completed: "check-circle",
  comment: "message-square",
};

export function AktivitetTab({ userId }: { userId: string }) {
  const { data, isLoading, isError, error } = useEmployeeActivity(userId);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center h-32">
          <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-destructive">
          Kunne ikke hente aktivitet: {(error as Error)?.message ?? "Ukjent feil"}
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          Ingen aktivitet siste 14 dager.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        <ul className="space-y-3">
          {data.map((item) => (
            <li
              key={`${item.type}-${item.id}`}
              className="flex items-start gap-3 text-sm border-b border-border/40 last:border-0 pb-3 last:pb-0"
            >
              <SrIcon
                name={ICON[item.type]}
                className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{item.title}</p>
                {item.detail && (
                  <p className="text-xs text-muted-foreground truncate">{item.detail}</p>
                )}
                {item.project_id && item.project_title && (
                  <Link
                    to={`/prosjekter/${item.project_id}`}
                    className="text-xs text-primary hover:underline"
                  >
                    {item.project_title}
                  </Link>
                )}
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                {formatDate(item.occurred_at)}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
